import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Person,
  School,
  Phone,
  Email,
  Home,
  CalendarToday,
  Assignment,
  EmojiEvents,
  TrendingUp,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

const StudentProfile = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const BRAND = {
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    gradientAlt: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    surface: 'rgba(255, 255, 255, 0.95)',
    ink: '#0f172a',
    muted: '#64748b',
  };

  useEffect(() => {
    fetchProfileData();
    fetchDashboardData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfileData(response.data);
    } catch (error) {
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/dashboard/student`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardData(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        background: BRAND.gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Paper sx={{
          p: 4,
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          textAlign: 'center',
        }}>
          <CircularProgress sx={{ color: '#f093fb', mb: 2 }} />
          <Typography variant="h6" sx={{ color: BRAND.ink }}>
            Loading profile...
          </Typography>
        </Paper>
      </Box>
    );
  }

  const studentInfo = dashboardData?.student_info;

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: BRAND.gradient,
      py: { xs: 3, md: 4 },
      px: { xs: 2, md: 4 },
    }}>
      <Container maxWidth="lg">
        {/* Profile Header */}
        <Paper sx={{
          mb: 4,
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          borderRadius: 4,
          boxShadow: '0 18px 70px rgba(0,0,0,0.28)',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.18)',
        }}>
          <Box sx={{
            background: BRAND.gradient,
            p: 4,
            position: 'relative',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{
                width: 100,
                height: 100,
                bgcolor: 'white',
                color: '#f093fb',
                fontSize: 48,
                fontWeight: 'bold',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              }}>
                {user?.name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h3" fontWeight="800" sx={{ color: 'white', mb: 1 }}>
                  {studentInfo?.full_name || user?.name}
                </Typography>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  {studentInfo?.student_index || 'Student'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label="Student"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.25)',
                      color: 'white',
                      fontWeight: 600,
                      backdropFilter: 'blur(10px)',
                    }}
                  />
                  {studentInfo?.class && (
                    <Chip 
                      label={`Primary: ${studentInfo.class}`}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: 'white',
                        fontWeight: 600,
                        backdropFilter: 'blur(10px)',
                      }}
                    />
                  )}
                  {studentInfo?.secondary_class && (
                    <Chip 
                      label={`Secondary: ${studentInfo.secondary_class}`}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: 'white',
                        fontWeight: 600,
                        backdropFilter: 'blur(10px)',
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Quick Stats */}
          <Grid container>
            <Grid item xs={12} sm={4} sx={{ 
              borderRight: { sm: '1px solid #e2e8f0' },
              borderBottom: { xs: '1px solid #e2e8f0', sm: 'none' },
            }}>
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h3" fontWeight="800" sx={{ 
                  background: BRAND.gradientAlt,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  {dashboardData?.overall_grade || 'N/A'}
                </Typography>
                <Typography variant="body1" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                  Overall Grade
                </Typography>
                {dashboardData?.overall_percentage !== undefined && (
                  <Typography variant="body2" sx={{ color: BRAND.muted, mt: 0.5 }}>
                    {dashboardData.overall_percentage}%
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ 
              borderRight: { sm: '1px solid #e2e8f0' },
              borderBottom: { xs: '1px solid #e2e8f0', sm: 'none' },
            }}>
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h3" fontWeight="800" sx={{ color: '#16a34a', mb: 1 }}>
                  {dashboardData?.attendance_percentage || 0}%
                </Typography>
                <Typography variant="body1" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                  Attendance
                </Typography>
                <Typography variant="body2" sx={{ color: BRAND.muted, mt: 0.5 }}>
                  {dashboardData?.exams_attended || 0}/{dashboardData?.total_exams || 0} Exams
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h3" fontWeight="800" sx={{ color: '#3b82f6', mb: 1 }}>
                  {dashboardData?.total_subjects || 0}
                </Typography>
                <Typography variant="body1" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                  Subjects
                </Typography>
                <Typography variant="body2" sx={{ color: BRAND.muted, mt: 0.5 }}>
                  Enrolled
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Personal Information */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{
              p: 4,
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: '1px solid rgba(255,255,255,0.18)',
              height: '100%',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  Personal Information
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: alpha('#667eea', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Person sx={{ color: '#667eea', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                      Full Name
                    </Typography>
                    <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.ink }}>
                      {studentInfo?.full_name || 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: alpha('#764ba2', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Assignment sx={{ color: '#764ba2', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                      Student Index
                    </Typography>
                    <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.ink }}>
                      {studentInfo?.student_index || 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: alpha('#f093fb', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Email sx={{ color: '#f093fb', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                      Email Address
                    </Typography>
                    <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.ink }}>
                      {profileData?.email || 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: alpha('#11998e', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Phone sx={{ color: '#11998e', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                      Phone Number
                    </Typography>
                    <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.ink }}>
                      {profileData?.student?.phone || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{
              p: 4,
              background: BRAND.surface,
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
              border: '1px solid rgba(255,255,255,0.18)',
              mb: 3,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  width: 4,
                  height: 32,
                  background: BRAND.gradient,
                  borderRadius: 2,
                  mr: 2
                }} />
                <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                  Academic Information
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: alpha('#667eea', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <School sx={{ color: '#667eea', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                      Primary Class
                    </Typography>
                    <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.ink }}>
                      {studentInfo?.class || 'Not Assigned'}
                    </Typography>
                  </Box>
                </Box>

                {studentInfo?.secondary_class && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: alpha('#764ba2', 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <School sx={{ color: '#764ba2', fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                          Secondary Class
                        </Typography>
                        <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.ink }}>
                          {studentInfo.secondary_class}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                <Divider />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: alpha('#f093fb', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <CalendarToday sx={{ color: '#f093fb', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                      O/L Year
                    </Typography>
                    <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.ink }}>
                      {profileData?.student?.ol_year || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Performance Stats */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card sx={{
                  background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                  color: 'white',
                  boxShadow: '0 8px 24px rgba(22, 163, 74, 0.3)',
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <EmojiEvents sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" fontWeight="800">
                      {dashboardData?.exams_passed || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Exams Passed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                  color: 'white',
                  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <TrendingUp sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" fontWeight="800">
                      {dashboardData?.average_marks || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Average Marks
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default StudentProfile;
